import { useState } from 'react';
import { formatDateTimeCH } from '../../utils/format';
import type { CreditNoteHistoryEntry } from '../../types/creditNotes';

/**
 * The audit trail: one entry per field that actually moved, newest first.
 *
 * A FIELD THAT DID NOT MOVE HAS NO ENTRY. The server compares `deleted.*` against `inserted.*`
 * inside the same statement as the change, so re-saving an unchanged form adds nothing. A trail
 * padded with non-events is a trail nobody reads, and the entries that matter would be lost in
 * it.
 *
 * THE "FROM" VALUE IS THE DATABASE'S, not the browser's. It comes out of the MERGE's `deleted`
 * row, so a form that posted a stale copy cannot write a false history of where the value
 * started.
 *
 * WHY THE REASON IS ON EVERY LINE. Decision 5 makes a justification mandatory on every change,
 * and the server copies it onto each field's row rather than storing it once per save. One entry
 * read on its own then still says why — which is the whole point of an audit somebody consults
 * months later, out of order.
 */

const TRACK_LABEL: Record<string, string> = {
    REASON: 'reason',
    REVIEW: 'booking review',
    RECOURSE: 'recourse',
};

/**
 * Readable names for the stored column names.
 *
 * The column name is kept as the fallback rather than being prettified by a rule: a field added
 * to a write path and forgotten here shows up as `SomeNewColumn` — visible and obviously
 * unfinished — instead of as a plausible-looking phrase nobody notices is wrong.
 */
const FIELD_LABEL: Record<string, string> = {
    ReasonCode: 'reason',
    Comment: 'note',
    Status: 'status',
    ReviewDueAt: 'follow-up date',
    CounterpartyType: 'counterparty',
    CounterpartyName: 'counterparty name',
    VendorId: 'vendor no.',
    ClaimedAmount: 'expected amount',
    ReceivedAmount: 'received amount',
    SubmittedOn: 'claim submitted',
    SettledOn: 'settled on',
    HubSpotTicketId: 'HubSpot ticket',
    Notes: 'note',
};

/**
 * "<track> <field>", with the track dropped when it would only repeat the field.
 *
 * The reason track's single field is the reason, so the naive concatenation read
 * "set reason reason GOODS_RETURN → CLAIM_DISCOUNT". The track is what makes a booking status
 * distinguishable from a recourse status, so it stays everywhere it adds something.
 */
function trackAndField(track: string, fieldName: string): string {
    const trackLabel = TRACK_LABEL[track] ?? track.toLowerCase();
    const fieldLabel = FIELD_LABEL[fieldName] ?? fieldName;
    return trackLabel === fieldLabel ? fieldLabel : `${trackLabel} ${fieldLabel}`;
}

const INITIAL_VISIBLE = 6;

export function CreditNoteTrail({ history }: { history: CreditNoteHistoryEntry[] }) {
    const [showAll, setShowAll] = useState(false);
    const visible = showAll ? history : history.slice(0, INITIAL_VISIBLE);

    return (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Trail</p>
                {history.length > INITIAL_VISIBLE && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="text-[11px] text-blue-600 hover:text-blue-800"
                    >
                        {showAll ? 'Show fewer' : `Show all ${history.length} entries`}
                    </button>
                )}
            </div>

            {history.length === 0 ? (
                <p className="text-[13px] text-gray-400">
                    Nothing has been recorded on this credit note yet. The trail fills as soon as a
                    reason, a booking verdict or a recourse case is saved.
                </p>
            ) : (
                <ul>
                    {visible.map(e => (
                        <li key={e.historyId} className="flex gap-2.5 border-b border-gray-50 py-1.5 last:border-b-0">
                            <span className="w-[118px] shrink-0 font-mono text-[11px] text-gray-400">
                                {formatDateTimeCH(e.changedAt)}
                            </span>
                            <span className="min-w-0 text-[12px] leading-[18px] text-gray-700">
                                <b className="font-medium">{e.changedBy}</b>
                                {' set '}
                                <span className="text-gray-800">
                                    {trackAndField(e.track, e.fieldName)}
                                    {e.caseNo !== null && ` (case ${e.caseNo})`}
                                </span>
                                {' '}
                                {/* An em dash where there was no previous value: "— →" reads as
                                    "from nothing", which is what an empty field was. */}
                                <span className="text-gray-400">{e.oldValue ?? '—'} →</span>
                                {' '}
                                <span className="text-gray-800">{e.newValue ?? '—'}</span>
                                {e.changeReason && (
                                    <span className="text-gray-400">{' · “'}{e.changeReason}{'”'}</span>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
